/**
 * Pinia decoration-FAMILY store -- the design detail page's data source.
 *
 * Card: Decoration color + family model (https://trello.com/c/2hcgDM7J)
 * Handoff: planning/design-handoffs/handoff_2026-08-23/
 *          2026-08-23-decoration-family-model/
 *
 * Reads `GET /api/artworks/<id>/family` (endpoint
 * api/src/endpoints/artworks.py:2248, service
 * api/src/services/decoration_family_view.py) -- one design's sizes
 * grouped by method, with approval at size grain and nothing above it.
 *
 * Why a second store beside `decorationDetail`
 * ============================================
 *
 * `useDecorationDetailStore` answers a different question with a
 * different grain: it fetches the SAFRS artwork record plus the
 * per-client x per-VERSION approval matrix that backs
 * `PerClientApprovalMatrix.vue`. That matrix keys on versions, which is
 * exactly the grain ADR 0067 rules out, and it stays on its own page
 * until its replacement ships (Jake's call). Fusing the two stores
 * would give one object two grains and hand a caller a version-grain
 * approval value on a surface whose whole point is that approval is
 * per size -- `docs/design-philosophy.md` principle 2. They share the
 * approval RULE, not the query.
 *
 * The single-fetch shape
 * ======================
 *
 * One endpoint, one call, one payload. The family read model was built
 * to be exactly what this page needs, so there is no client-side
 * assembly of several endpoints and therefore no window in which the
 * page shows a half-consistent design.
 *
 * Not-found is one state, deliberately
 * ====================================
 *
 * The endpoint answers 404 identically for "no such artwork",
 * "soft-deleted" and "owned by another distributor" -- a 403 on the
 * last would confirm the row exists and hand an attacker an
 * enumeration oracle over another tenant's artwork ids. The store
 * preserves that: `notFound` is one boolean and the page renders one
 * message. Do not add a variant that distinguishes them.
 *
 * Tenant + epoch hygiene
 * ======================
 *
 * Mirrors `useDecorationDetailStore` / `useArtworksStore`: an integer
 * epoch is bumped by `clear()` so a fetch still in flight when
 * impersonation stops cannot repopulate state with the prior tenant's
 * design.
 */
import { defineStore } from "pinia";
import { ref } from "vue";
import api from "@/services/api";

function _errMessage(err) {
    return (
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        "Could not load this design."
    );
}

export const useDecorationFamilyStore = defineStore("decorationFamily", () => {
    // ---- state ----
    const family = ref(null);
    const loading = ref(false);
    const error = ref(null);
    const notFound = ref(false);

    let epoch = 0;

    function _isCurrentEpoch(captured) {
        return captured === epoch;
    }

    // ---- actions ----

    /**
     * Load one design's family payload.
     *
     * Resolves rather than throws on failure: the page renders the
     * error or not-found state from the refs, and a rejected promise
     * would additionally surface as an unhandled rejection in the
     * route guard that calls this.
     *
     * @param {number|string} artworkId
     * @returns {Promise<object|null>} the payload, or null on failure
     */
    async function fetchFamily(artworkId) {
        const captured = epoch;
        loading.value = true;
        error.value = null;
        notFound.value = false;
        try {
            const resp = await api.get(`/api/artworks/${artworkId}/family`);
            if (!_isCurrentEpoch(captured)) return null;
            family.value = resp?.data ?? null;
            return family.value;
        } catch (err) {
            if (!_isCurrentEpoch(captured)) return null;
            family.value = null;
            if (err?.response?.status === 404) {
                // One state for all three causes -- see the header.
                notFound.value = true;
            } else {
                error.value = _errMessage(err);
            }
            return null;
        } finally {
            if (_isCurrentEpoch(captured)) {
                loading.value = false;
            }
        }
    }

    /**
     * Drop everything and invalidate any in-flight fetch.
     */
    function clear() {
        epoch += 1;
        family.value = null;
        loading.value = false;
        error.value = null;
        notFound.value = false;
    }

    return {
        family,
        loading,
        error,
        notFound,
        fetchFamily,
        clear,
    };
});

export default useDecorationFamilyStore;
